import { supabase } from "../integrations/supabase/client";
import { DbWorkout, DbWorkoutLog } from "../types/database";
import {
  ExerciseResult,
  validateWorkout,
  Workout,
  WorkoutLog,
  WorkoutLogSchema,
  WorkoutSchema,
} from "../schemas/workout";
import { getCurrentUserIdSafe } from "../utils/auth";
import { generateId } from "@/utils/id";

// Add this near the top of the file with other imports
export type WorkoutHistoryItem = {
  id: string;
  date: Date;
  workout_name: string;
  status: "completed" | "in_progress" | "cancelled";
  exercises_completed: number;
  total_exercises: number;
  duration?: number;
  workout_id?: string;
};

// Get the current user ID
async function getCurrentUserId(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) throw new Error("You must be logged in");
  return session.user.id;
}

// Workout service with business logic
const workoutService = {
  // Get all workouts for the current user
  async getMyWorkouts(): Promise<DbWorkout[]> {
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
      .from("user_workouts")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) throw new Error(`Failed to fetch workouts: ${error.message}`);

    // Validate each workout with Zod
    return (data || []).map((workout) => validateWorkout(workout));
  },

  // Get a workout by ID
  async getWorkout(id: string): Promise<Workout | null> {
    try {
      console.log("Fetching workout with ID:", id);

      const { data, error } = await supabase
        .from("user_workouts")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching workout:", error);

        // Check if it's a "no rows returned" error
        if (error.code === "PGRST116") {
          console.log("No workout found with ID:", id);
          return null;
        }

        throw new Error(`Failed to fetch workout: ${error.message}`);
      }

      if (!data) {
        console.log("No workout data returned for ID:", id);
        return null;
      }

      console.log("Workout fetched successfully:", data);
      return WorkoutSchema.parse(data);
    } catch (error) {
      console.error("Error in getWorkout:", error);
      throw error;
    }
  },

  // Create a new workout
  async createWorkout(workout: Partial<DbWorkout>): Promise<DbWorkout> {
    try {
      // Ensure we have a user ID as a string
      let userId = workout.user_id;

      if (!userId) {
        userId = await getCurrentUserId();
      }

      // Make sure userId is a string
      if (typeof userId !== "string") {
        throw new Error("Invalid user ID format");
      }

      // Create a new workout object with the correct structure
      const newWorkout = {
        id: generateId(),
        user_id: userId,
        name: workout.name || "New Workout",
        description: workout.description || "",
        exercises: workout.exercises || [],
        created_at: new Date().toISOString(),
        modified_at: new Date().toISOString(),
        // Add other required fields with default values
        difficulty: "medium",
        equipment: [],
        estimated_duration: 0,
        target_muscle_groups: [],
        type: "strength",
      };

      // Validate the workout
      const validatedWorkout = validateWorkout(newWorkout);

      // Insert into database
      const { data, error } = await supabase
        .from("user_workouts")
        // @ts-expect-error - TODO: fix this
        .insert(validatedWorkout)
        .select()
        .single();

      if (error) throw new Error(`Failed to create workout: ${error.message}`);
      return WorkoutSchema.parse(data);
    } catch (error) {
      console.error("Error in createWorkout:", error);
      throw error;
    }
  },

  // Update an existing workout
  async updateWorkout(id: string, updates: Partial<DbWorkout>) {
    const userId = await getCurrentUserId();

    // Verify ownership
    const { data: workout } = await supabase
      .from("user_workouts")
      .select("user_id")
      .eq("id", id)
      .single();

    if (!workout) throw new Error("Workout not found");
    if (workout.user_id !== userId) {
      throw new Error("You can only update your own workouts");
    }

    console.log(
      "API service - Updating workout with exercises:",
      updates.exercises
    );
    console.log(
      "API service - Exercise IDs:",
      updates.exercises?.map((e) => e.id)
    );

    const { data, error } = await supabase
      .from("user_workouts")
      .update({
        ...(updates.name && { name: updates.name }),
        ...(updates.description && { description: updates.description }),
        ...(updates.exercises && { exercises: updates.exercises }),
        modified_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) throw new Error(`Failed to update workout: ${error.message}`);
    return WorkoutSchema.parse(data);
  },

  // Save a workout (create or update)
  async saveWorkout(workout: DbWorkout): Promise<DbWorkout> {
    const userId = await getCurrentUserId();

    // Ensure the workout belongs to the current user
    if (workout.user_id !== userId) {
      throw new Error("You can only modify your own workouts");
    }

    // Check if the workout exists
    const { data: existingWorkout } = await supabase
      .from("user_workouts")
      .select("id")
      .eq("id", workout.id)
      .maybeSingle();

    if (existingWorkout) {
      // Update existing workout
      const { data, error } = await supabase
        .from("user_workouts")
        .update({
          name: workout.name,
          description: workout.description,
          exercises: workout.exercises,
          modified_at: new Date().toISOString(),
        })
        .eq("id", workout.id)
        .select()
        .single();

      if (error) throw new Error(`Failed to update workout: ${error.message}`);
      return WorkoutSchema.parse(data);
    } else {
      // Create new workout
      const { data, error } = await supabase
        .from("user_workouts")
        // @ts-expect-error - TODO: fix this
        .insert({
          ...workout,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw new Error(`Failed to create workout: ${error.message}`);
      return WorkoutSchema.parse(data);
    }
  },

  // Delete a workout
  async deleteWorkout(id: string): Promise<void> {
    const userId = await getCurrentUserId();

    // Verify ownership
    const { data: workout } = await supabase
      .from("user_workouts")
      .select("user_id")
      .eq("id", id)
      .single();

    if (!workout) throw new Error("Workout not found");
    if (workout.user_id !== userId) {
      throw new Error("You can only delete your own workouts");
    }

    const { error } = await supabase
      .from("user_workouts")
      .delete()
      .eq("id", id);

    if (error) throw new Error(`Failed to delete workout: ${error.message}`);
  },

  // Create a workout log
  async createWorkoutLog(workout: DbWorkout, userId: string): Promise<string> {
    try {
      // Create a new workout log entry with a proper UUID
      const logId = crypto.randomUUID();

      // Create a log entry with fields that match the database schema
      const logEntry = {
        id: logId,
        user_id: userId,
        workout_id: workout.id,
        workout_name: workout.name,
        status: "in_progress",
        started_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        // Store the entire workout as a JSON snapshot
        workout_snapshot: workout,
      };

      const { error } = await supabase
        .from("workout_logs")
        // @ts-expect-error - TODO: fix this
        .insert(logEntry);

      if (error) {
        console.error("Error creating workout log:", error);
        throw new Error(`Failed to create workout log: ${error.message}`);
      }

      return logId;
    } catch (error) {
      console.error("Error creating workout log:", error);
      throw error;
    }
  },

  async overrideWorkoutLog(id: string, workout: Partial<DbWorkout>) {
    const { error } = await supabase
      .from("workout_logs")
      .update({
        ...workout,
      })
      .eq("id", id);
    if (error) throw new Error(`Failed to override workout log: ${error.message}`);
  },

  // Get workout logs for the current user
  async getWorkoutLogs() {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from("workout_logs")
      .select("*")
      .eq("user_id", userId)
      .order("started_at", { ascending: false });

    if (error)
      throw new Error(`Failed to fetch workout logs: ${error.message}`);

    // Return data without validation for now
    return data || [];
  },

  // Get raw workout logs for debugging
  async getRawWorkoutLogs() {
    try {
      const userId = await getCurrentUserIdSafe();

      if (!userId) {
        console.log("No user ID found for getRawWorkoutLogs");
        return [];
      }

      // Get raw workout logs without any processing or validation
      const { data, error } = await supabase
        .from("workout_logs")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching raw workout logs:", error);
        throw error;
      }

      console.log(`Retrieved ${data?.length || 0} raw workout logs`);
      return data || [];
    } catch (error) {
      console.error("Error in getRawWorkoutLogs:", error);
      throw error;
    }
  },

  // Get a specific workout log
  async getWorkoutLog(id: string) {
    // First try to get the workout log from the workout_logs table
    const { data: logData } = await supabase
      .from("workout_logs")
      .select("*")
      .eq("id", id)
      .single();

    if (!logData) {
      throw new Error(`Workout log not found: ${id}`);
    }

    return WorkoutLogSchema.parse(logData);
  },

  // Update a workout log
  async updateWorkoutLog(
    id: string,
    logsArgs: { workoutSnapshot: WorkoutLog["workout_snapshot"] }
  ) {
    const { data, error } = await supabase
      .from("workout_logs")
      .update({
        workout_snapshot: logsArgs.workoutSnapshot,
      })
      .eq("id", id)
      .select()
      .single();

    console.log("SUPABASE - Updated workout log:", data);
    if (error)
      throw new Error(`Failed to update workout log: ${error.message}`);
    return data;
  },

  // Complete a workout log
  async completeWorkoutLog(
    id: string,
    exerciseResults: {
      workoutSnapshot: WorkoutLog["workout_snapshot"];
    }
  ) {
    const { data, error } = await supabase
      .from("workout_logs")
      .update({
        completed_at: new Date().toISOString(),
        status: "completed",
        // Update the workout_snapshot with the exercise results
        workout_snapshot: exerciseResults.workoutSnapshot,
      })
      .eq("id", id)
      .select()
      .single();

    if (error)
      throw new Error(`Failed to complete workout log: ${error.message}`);
    return data;
  },

  // Start a workout log
  async startWorkoutLog(
    workoutId: string,
    workoutSnapshot: WorkoutLog
  ): Promise<WorkoutLog> {
    try {
      console.log("Creating workout log for workout:", workoutId);

      const { data, error } = await supabase
        .from("workout_logs")
        // @ts-expect-error - TODO: fix this
        .insert(workoutSnapshot)
        .select()
        .single();

      if (error) {
        console.error("Error creating workout log:", error);
        throw new Error(`Failed to create workout log: ${error.message}`);
      }

      if (!data) {
        throw new Error("No data returned after creating workout log");
      }

      console.log("Workout log created successfully:", data);
      return WorkoutLogSchema.parse(data);
    } catch (error) {
      console.error("Error in startWorkoutLog:", error);
      throw error;
    }
  },

  // Get workout history
  async getWorkoutHistory() {
    try {
      const userId = await getCurrentUserIdSafe();

      if (!userId) {
        throw new Error("User not authenticated");
      }

      // Get workout logs
      const { data: logsData, error: logsError } = await supabase
        .from("workout_logs")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (logsError) throw logsError;
      console.log("Raw workout logs data:", logsData);

      // Return the raw data for now - we'll handle the mapping in the component
      // This avoids validation errors that might be causing the page to fail
      return logsData;
    } catch (error) {
      console.error("Error fetching workout history:", error);
      throw error;
    }
  },

  // Delete a workout log by ID
  async deleteWorkoutLog(id: string): Promise<void> {
    const userId = await getCurrentUserId();
    // Only allow deleting logs that belong to the current user
    const { data: log } = await supabase
      .from("workout_logs")
      .select("user_id")
      .eq("id", id)
      .single();
    if (!log) throw new Error("Workout log not found");
    if (log.user_id !== userId) {
      throw new Error("You can only delete your own workout logs");
    }
    const { error } = await supabase
      .from("workout_logs")
      .delete()
      .eq("id", id);
    if (error) throw new Error(`Failed to delete workout log: ${error.message}`);
  },

  deleteIncompleteWorkoutLogs: async (): Promise<void> => {
    console.log("Deleting incomplete workout logs");
    const { error } = await supabase
      .from("workout_logs")
      .delete()
      .eq("status", "in_progress");

    if (error) throw new Error(error.message);
  },
};

export { workoutService, WorkoutHistoryItem, WorkoutLogSchema };
